import { Link } from "react-router-dom";
import type { Product } from "@neus/shared";
import { useEffectivePrice, useStock } from "../../api/hooks";
import { useCart } from "../../store/cart";
import { ContractorTag, Price, StockBadge } from "./bits";
import styles from "./Product.module.css";

/** `preview`: from the build's catalog, until the live data arrives; so no price, stock or cart button yet. */
export function ProductCard({ product, preview }: { product: Product; preview?: boolean }) {
  const stock = useStock(product);
  const pricing = useEffectivePrice(product);
  const contractor = pricing?.regularPrice !== undefined;
  const add = useCart((s) => s.add);
  const inCart = useCart((s) => s.items.find((i) => i.sku === product.sku)?.qty ?? 0);
  const image = product.images[0];
  const onSale = product.compareAtPrice !== undefined && product.compareAtPrice > product.price;

  return (
    <article className={contractor ? `${styles.card} ${styles.contractorCard}` : styles.card}>
      <Link to={`/products/${encodeURIComponent(product.sku)}`} className={styles.cardImage}>
        {image ? (
          <img src={image.url} alt={image.alt || product.name} loading="lazy" />
        ) : (
          <div className={styles.noImage} />
        )}
        {onSale && !contractor && <span className={styles.saleTag}>Sale</span>}
        {contractor && <ContractorTag />}
      </Link>
      <div className={styles.cardBody}>
        <span className={styles.brand}>{product.brand}</span>
        <h3 className={styles.cardTitle}>
          <Link to={`/products/${encodeURIComponent(product.sku)}`}>{product.name}</Link>
        </h3>
        <span className={styles.sku}>SKU {product.sku}</span>
        {preview ? (
          <div className={styles.previewSpace} />
        ) : (
          <>
            <div className={styles.cardMeta}>
              <Price
                price={pricing?.price ?? product.price}
                compareAt={product.compareAtPrice}
                regularPrice={pricing?.regularPrice}
              />
              <StockBadge stock={stock} />
            </div>
            <button type="button" className="btn btn--block" disabled={stock <= inCart} onClick={() => add(product, 1)}>
              {stock <= 0
                ? "Out of stock"
                : stock <= inCart
                  ? "Max in cart"
                  : inCart
                    ? `Add another (${inCart} in cart)`
                    : "Add to cart"}
            </button>
          </>
        )}
      </div>
    </article>
  );
}

export function ProductGrid({ products, preview }: { products: Product[]; preview?: boolean }) {
  return (
    <div className={styles.grid} aria-busy={preview || undefined}>
      {products.map((p) => (
        <ProductCard key={p.sku} product={p} preview={preview} />
      ))}
    </div>
  );
}

export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className={styles.grid} aria-busy="true" aria-label="Loading products">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={`${styles.card} ${styles.skeleton}`} />
      ))}
    </div>
  );
}
